import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ExperimentProjection, MetricSeries, RunProjection } from "@roboscientist/contracts";
import { Panel } from "@roboscientist/ui";
import { api } from "../api.js";
import { useCockpitStore } from "../store.js";
import { ExperimentMetricComparison } from "./ExperimentMetricComparison.js";
import { ExperimentProgressIntro } from "./ExperimentProgressIntro.js";
import { progressContextForRun } from "./experimentProgressPresentation.js";
import { TelemetryRunSelector } from "./TelemetryRunSelector.js";
import { TrainingChart, type TrainingReplayPresentation } from "./TrainingChart.js";

export function ExperimentTelemetryPanel({
  runs,
  selectedRunId,
  onSelectRun,
  title = "Experiment telemetry",
  eyebrow = "SELECT A RUN · CURVE VIEW",
  className = "",
  compact = false,
  progressExperiments = [],
  compareExperiments = [],
  compareRuns = [],
  enableCompare = false,
  metricSeries,
  readOnly = false,
  replay
}: {
  runs: RunProjection[];
  selectedRunId: string | null;
  onSelectRun: (runId: string) => void;
  title?: string;
  eyebrow?: string;
  className?: string;
  compact?: boolean;
  progressExperiments?: ExperimentProjection[];
  compareExperiments?: ExperimentProjection[];
  compareRuns?: RunProjection[];
  enableCompare?: boolean;
  metricSeries?: MetricSeries[];
  readOnly?: boolean;
  replay?: TrainingReplayPresentation;
}) {
  const storedMode = useCockpitStore((state) => state.experimentTelemetryMode);
  const setMode = useCockpitStore((state) => state.setExperimentTelemetryMode);
  const mode = enableCompare ? storedMode : "single";
  const selectedRun = runs.find((run) => run.id === selectedRunId) ?? null;
  const progressExperiment = selectedRun
    ? progressExperiments.find((experiment) => experiment.id === selectedRun.experimentId) ?? null
    : null;
  const progressContext = selectedRun && progressExperiment
    ? progressContextForRun(progressExperiment, selectedRun)
    : null;
  const metrics = useQuery({
    queryKey: ["metrics", selectedRunId],
    queryFn: () => api.metrics(selectedRunId ?? ""),
    enabled: !readOnly && Boolean(selectedRun),
    refetchInterval: 5_000
  });

  const openRun = !readOnly && selectedRun ? <Link to="/runs/$runId" params={{ runId: selectedRun.id }} className="text-link">Open full Run →</Link> : null;
  const action = enableCompare ? <div className="telemetry-panel-actions">
    <div className="segmented telemetry-mode-control" aria-label="Telemetry view mode">
      <button className={mode === "single" ? "selected" : ""} aria-pressed={mode === "single"} onClick={() => setMode("single")}>Single</button>
      <button className={mode === "compare" ? "selected" : ""} aria-pressed={mode === "compare"} onClick={() => setMode("compare")}>Compare</button>
    </div>
    {mode === "single" ? openRun : null}
  </div> : openRun;

  return <Panel
    title={title}
    eyebrow={mode === "compare" ? "MANUAL CROSS-EXPERIMENT · CURVE VIEW" : eyebrow}
    className={className}
    action={action}
  >
    <div hidden={mode !== "single"}>
      <ExperimentProgressIntro context={progressContext} />
      <TelemetryRunSelector runs={runs} selectedRunId={selectedRunId} onSelect={onSelectRun} />
      <TrainingChart run={selectedRun} series={metricSeries ?? metrics.data?.series ?? []} compact={compact} replay={readOnly ? replay : undefined} />
    </div>
    {enableCompare ? <div hidden={mode !== "compare"}>
      <ExperimentMetricComparison experiments={compareExperiments} runs={compareRuns} />
    </div> : null}
  </Panel>;
}
